/**
 * TenantAccountTab — Hesap bilgisi + sahip + takım + güvenlik + tehlikeli bölge (sil).
 *
 * `onDeleted` prop'u verildiğinde, silme işlemi başarılı olduktan sonra parent
 * sayfanın yönlendirmeyi tetiklemesi beklenir (ör. hesaplar listesine dön).
 */
import { useState } from "react";
import { toast } from "sonner";
import { Card, CardHeader, Mono } from "@/components/design-system";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel,
    AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
    AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Users, UserRound, ShieldCheck, Clock, History, Trash2, AlertTriangle, Loader2 } from "lucide-react";
import { adminApi } from "@/lib/api";
import { fmtDateTime, relTime, readError } from "../_tenant-helpers";

export default function TenantAccountTab({ data, onDeleted }) {
    const owner = data.owner;
    const team = data.team || [];
    const security = data.security || {};
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [typed, setTyped] = useState("");
    const [deleting, setDeleting] = useState(false);
    const canDelete = typed.trim() === (data.company_name || "").trim() && !deleting;

    const handleDelete = async () => {
        if (!canDelete) return;
        setDeleting(true);
        try {
            await adminApi.delete(`/admin/tenants/${data.id}`);
            toast.success(`${data.company_name} ve tüm verileri silindi.`);
            setConfirmOpen(false);
            if (typeof onDeleted === "function") onDeleted();
        } catch (err) {
            toast.error(readError(err));
            setDeleting(false);
        }
    };

    return (
        <div className="space-y-5" data-testid="account-tab">
            {/* Hesap üst bilgileri */}
            <Card>
                <CardHeader
                    title="Firma Bilgileri"
                    desc="Hesabın temel kayıtları — yeni açılış sırasında girilen alanlar."
                />
                <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <KV label="Firma Adı" value={data.company_name} testId="account-company" />
                    <KV label="Alan Adı" value={data.site_domain} testId="account-domain" />
                    <KV label="Site Key" value={data.site_key} mono testId="account-sitekey" />
                    <KV label="Oluşturulma" value={fmtDateTime(data.created_at)} testId="account-created" />
                    <KV label="Durum" value={data.status === "active" ? "Aktif" : "Askıda"} testId="account-status" />
                    <KV label="Widget" value={data.widget_disabled ? "Kapalı" : "Yayında"} testId="account-widget" />
                </div>
            </Card>

            {/* Sahip */}
            <Card>
                <CardHeader
                    title="Sahip"
                    desc="İlk giriş e-postası owner üzerinden gönderilir."
                />
                <div className="p-5">
                    {owner ? (
                        <div className="flex items-start gap-3" data-testid="account-owner">
                            <div className="h-9 w-9 rounded-lg border border-border bg-muted/40 grid place-items-center shrink-0">
                                <UserRound className="h-4 w-4" strokeWidth={1.75} />
                            </div>
                            <div className="min-w-0 flex-1 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-2">
                                <KV label="Ad Soyad" value={owner.name || "—"} />
                                <KV label="E-posta" value={owner.email} />
                                <KV label="Rol" value={owner.role || "owner"} />
                                <KV label="Durum" value={owner.status === "active" ? "Aktif" : owner.status} />
                                <KV label="Son Giriş"
                                    value={owner.last_login_at ? `${relTime(owner.last_login_at)}` : "—"}
                                    hint={owner.last_login_at ? fmtDateTime(owner.last_login_at) : null} />
                                <KV label="Kayıt Tarihi" value={fmtDateTime(owner.created_at)} />
                            </div>
                        </div>
                    ) : (
                        <div className="text-xs text-muted-foreground italic" data-testid="account-no-owner">
                            Bu hesabın tanımlı sahibi yok.
                        </div>
                    )}
                </div>
            </Card>

            {/* Takım */}
            <Card>
                <CardHeader
                    title="Takım Üyeleri"
                    desc={`Owner dışındaki üyeler · ${team.length} kişi`}
                    right={<Users className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />}
                />
                <div className="p-5">
                    {team.length === 0 ? (
                        <div className="text-xs text-muted-foreground italic" data-testid="account-no-team">
                            Henüz takım üyesi yok.
                        </div>
                    ) : (
                        <table className="w-full text-sm" data-testid="account-team-table">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground border-b border-border">
                                    <th className="pb-2 pr-3">Ad</th>
                                    <th className="pb-2 pr-3">E-posta</th>
                                    <th className="pb-2 pr-3">Rol</th>
                                    <th className="pb-2 pr-3">Son Giriş</th>
                                    <th className="pb-2">Durum</th>
                                </tr>
                            </thead>
                            <tbody>
                                {team.map((u) => (
                                    <tr key={u.id} className="border-b border-border last:border-b-0">
                                        <td className="py-2 pr-3 text-[13px]">{u.name || "—"}</td>
                                        <td className="py-2 pr-3 text-xs text-muted-foreground">{u.email}</td>
                                        <td className="py-2 pr-3 text-xs">{u.role}</td>
                                        <td className="py-2 pr-3 text-xs text-muted-foreground">
                                            {u.last_login_at ? relTime(u.last_login_at) : "—"}
                                        </td>
                                        <td className="py-2 text-xs">{u.status === "active" ? "Aktif" : u.status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </Card>

            {/* Güvenlik */}
            <Card>
                <CardHeader
                    title="Güvenlik & Erişim"
                    right={<ShieldCheck className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />}
                />
                <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                    <SecurityRow
                        icon={Clock}
                        title="Son 30 Günde Başarısız OTP"
                        value={`${security.failed_logins_30d || 0} deneme`}
                        tone={(security.failed_logins_30d || 0) > 5 ? "warn" : "muted"}
                        testId="security-failed"
                    />
                    <SecurityRow
                        icon={History}
                        title="Son Süperadmin Impersonation"
                        value={security.last_impersonation ? relTime(security.last_impersonation.at) : "Hiç yok"}
                        hint={security.last_impersonation ? fmtDateTime(security.last_impersonation.at) : null}
                        testId="security-impersonation"
                    />
                </div>
            </Card>

            {/* Tehlikeli Bölge — kalıcı silme */}
            <Card className="border-rose-300/70" data-testid="danger-zone-card">
                <CardHeader
                    title="Tehlikeli Bölge"
                    desc="Hesabı kalıcı olarak sil. Bu işlem geri alınamaz."
                    right={<AlertTriangle className="h-4 w-4 text-rose-600" strokeWidth={1.75} />}
                />
                <div className="p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="text-xs text-muted-foreground max-w-xl">
                        Firma, sahibi, takım üyeleri, XML feed'leri, beden tabloları, ürün eşleşmeleri ve tüm
                        log kayıtları silinir. Widget script'i artık <Mono className="!text-[11px]">unknown site key</Mono> döner.
                    </div>
                    <Button
                        variant="outline"
                        className="h-9 border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                        onClick={() => { setTyped(""); setConfirmOpen(true); }}
                        data-testid="danger-delete-btn"
                    >
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.75} />
                        Hesabı Kalıcı Olarak Sil
                    </Button>
                </div>
            </Card>

            <AlertDialog open={confirmOpen} onOpenChange={(o) => { if (!deleting) setConfirmOpen(o); }}>
                <AlertDialogContent data-testid="danger-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-rose-700">
                            <AlertTriangle className="h-5 w-5" strokeWidth={1.75} />
                            Hesabı silmeyi onayla
                        </AlertDialogTitle>
                        <AlertDialogDescription className="space-y-3">
                            <span className="block">
                                Devam etmek için firma adını birebir yaz:
                                {" "}<b className="text-foreground">{data.company_name}</b>
                            </span>
                            <Input
                                autoFocus
                                value={typed}
                                onChange={(e) => setTyped(e.target.value)}
                                placeholder={data.company_name}
                                className="h-9 text-sm"
                                data-testid="danger-delete-input"
                                disabled={deleting}
                            />
                            <span className="block text-[11px] text-muted-foreground">
                                Silme kalıcıdır. Site key çalışmayı durdurur, veritabanındaki
                                tüm tenant kayıtları temizlenir.
                            </span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting} data-testid="danger-delete-cancel">
                            Vazgeç
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); handleDelete(); }}
                            disabled={!canDelete}
                            className="bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-500"
                            data-testid="danger-delete-confirm"
                        >
                            {deleting ? (
                                <>
                                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                    Siliniyor…
                                </>
                            ) : (
                                <>
                                    <Trash2 className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.75} />
                                    Kalıcı Olarak Sil
                                </>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function KV({ label, value, mono, hint, testId }) {
    return (
        <div data-testid={testId}>
            <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                {label}
            </div>
            <div className="text-[13px] font-medium mt-0.5 break-all">
                {mono ? <Mono className="!text-[12px] !text-foreground">{value}</Mono> : (value || "—")}
            </div>
            {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
        </div>
    );
}

function SecurityRow({ icon: Icon, title, value, hint, tone = "muted", testId }) {
    const valueCls = tone === "warn" ? "text-amber-600" : "text-foreground";
    return (
        <div className="flex items-start gap-3" data-testid={testId}>
            <div className="h-8 w-8 rounded-md border border-border bg-muted/40 grid place-items-center shrink-0">
                <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
            </div>
            <div className="min-w-0">
                <div className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">{title}</div>
                <div className={`text-sm font-medium ${valueCls}`}>{value}</div>
                {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
            </div>
        </div>
    );
}
